// ============================================================================
// RECIPE BOM TAB COMPONENT
// Bill of Materials tracking for recipe entities
// Date: January 20, 2025
// ============================================================================

'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChefHat, Package, DollarSign, Hash, Trash2, Plus } from 'lucide-react';
import { authService } from '@/lib/authService';
import { useTheme } from '@/contexts/ThemeContext';
import { confirm } from '@/lib/notifications';

// ============================================================================
// TYPES
// ============================================================================

interface BOMItem {
  id: string;
  ingredient_name: string;
  quantity: number;
  unit: string;
  unit_cost?: number;
  total_cost?: number;
  supplier?: string;
  notes?: string;
  sort_order: number;
  recorded_at: string;
}

interface RecipeBOMTabProps {
  workItemId: string;
  isActive: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getAuthToken = (): string | null => {
  return authService.getTokens()?.access_token || null;
};

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR'
  }).format(amount);
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function RecipeBOMTab({ workItemId, isActive }: RecipeBOMTabProps) {
  const [items, setItems] = useState<BOMItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState({
    ingredient_name: '',
    quantity: '',
    unit: 'kg',
    unit_cost: '',
    supplier: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const hasLoadedRef = useRef(false);
  const { currentTheme } = useTheme();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/work-items/${workItemId}/bom?limit=100`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setItems(result.data || []);
        }
      }
    } catch (error) {
      console.error('Error loading BOM data:', error);
    } finally {
      setLoading(false);
    }
  }, [workItemId]);

  useEffect(() => {
    hasLoadedRef.current = false;
  }, [workItemId]);

  useEffect(() => {
    if (isActive && !hasLoadedRef.current) {
      loadData();
      hasLoadedRef.current = true;
    }
  }, [isActive, loadData]);

  const handleAddItem = async () => {
    if (!newItem.ingredient_name || !newItem.quantity) return;

    try {
      setSubmitting(true);
      const quantity = parseFloat(newItem.quantity);
      const unit_cost = newItem.unit_cost ? parseFloat(newItem.unit_cost) : null;
      const total_cost = unit_cost ? quantity * unit_cost : null;

      const response = await fetch(`/api/v1/work-items/${workItemId}/bom`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ingredient_name: newItem.ingredient_name,
          quantity: quantity,
          unit: newItem.unit,
          unit_cost: unit_cost,
          total_cost: total_cost,
          supplier: newItem.supplier || null,
          notes: newItem.notes || null,
          sort_order: items.length,
          recorded_at: new Date().toISOString()
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setItems([...items, result.data]);
          setNewItem({
            ingredient_name: '',
            quantity: '',
            unit: 'kg',
            unit_cost: '',
            supplier: '',
            notes: ''
          });
        }
      }
    } catch (error) {
      console.error('Error adding BOM item:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!(await confirm({ message: 'Delete this ingredient?', variant: 'danger' }))) return;

    try {
      const response = await fetch(`/api/v1/work-items/${workItemId}/bom?id=${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      if (response.ok) {
        setItems(items.filter(i => i.id !== id));
      }
    } catch (error) {
      console.error('Error deleting BOM item:', error);
    }
  };

  const isDark = currentTheme.mode === 'dark';

  // Calculate totals
  const totalCost = items.reduce((sum, item) => sum + (item.total_cost || 0), 0);
  const totalItems = items.length;

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-12 bg-gray-200 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* BOM Summary */}
      {items.length > 0 && (
        <div className={`p-6 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Total Ingredients
              </div>
              <div className={`text-2xl font-bold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                {totalItems}
              </div>
            </div>
            <div>
              <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Total Recipe Cost
              </div>
              <div className={`text-2xl font-bold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                {formatCurrency(totalCost)}
              </div>
            </div>
            <div>
              <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Cost per Serving
              </div>
              <div className={`text-2xl font-bold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                {formatCurrency(totalCost / (items[0]?.quantity || 1))}
              </div>
              <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                Based on yield quantity
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add New Ingredient */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <h4 className={`font-medium mb-3 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
          Add Ingredient
        </h4>
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Ingredient Name *
              </label>
              <input
                type="text"
                value={newItem.ingredient_name}
                onChange={(e) => setNewItem({ ...newItem, ingredient_name: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                placeholder="e.g., All-purpose flour, Chicken breast"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Quantity *
              </label>
              <input
                type="number"
                step="0.01"
                value={newItem.quantity}
                onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Unit
              </label>
              <select
                value={newItem.unit}
                onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              >
                <option value="kg">kg</option>
                <option value="g">g</option>
                <option value="L">L</option>
                <option value="ml">ml</option>
                <option value="cup">cup</option>
                <option value="tbsp">tbsp</option>
                <option value="tsp">tsp</option>
                <option value="piece">piece</option>
                <option value="unit">unit</option>
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Unit Cost (ZAR)
              </label>
              <input
                type="number"
                step="0.01"
                value={newItem.unit_cost}
                onChange={(e) => setNewItem({ ...newItem, unit_cost: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                placeholder="0.00"
              />
            </div>
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Supplier
            </label>
            <input
              type="text"
              value={newItem.supplier}
              onChange={(e) => setNewItem({ ...newItem, supplier: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              placeholder="Supplier name"
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Notes
            </label>
            <textarea
              value={newItem.notes}
              onChange={(e) => setNewItem({ ...newItem, notes: e.target.value })}
              className={`w-full px-3 py-2 border rounded-lg ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              rows={2}
              placeholder="Preparation notes, substitutions, etc."
            />
          </div>
          <button
            onClick={handleAddItem}
            disabled={submitting || !newItem.ingredient_name || !newItem.quantity}
            className="w-full px-4 py-2 text-white rounded-lg disabled:opacity-50 hover:opacity-90 transition-colors"
            style={{ backgroundColor: currentTheme.colors.primary }}
          >
            {submitting ? 'Adding...' : 'Add Ingredient'}
          </button>
        </div>
      </div>

      {/* BOM Table */}
      {items.length === 0 ? (
        <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          No ingredients added yet
        </div>
      ) : (
        <div className={`rounded-lg overflow-hidden border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <table className="w-full">
            <thead className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
              <tr>
                <th className={`px-4 py-3 text-left text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  #
                </th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Ingredient
                </th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Quantity
                </th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Unit Cost
                </th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Total
                </th>
                <th className={`px-4 py-3 text-left text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Supplier
                </th>
                <th className={`px-4 py-3 text-right text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {items.map((item, index) => (
                <tr key={item.id} className={isDark ? 'bg-gray-900 hover:bg-gray-800' : 'bg-white hover:bg-gray-50'}>
                  <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {index + 1}
                  </td>
                  <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                    <div className="font-medium">{item.ingredient_name}</div>
                    {item.notes && (
                      <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        {item.notes}
                      </div>
                    )}
                  </td>
                  <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                    <span className="font-mono">{item.quantity}</span>
                    <span className="text-gray-500 ml-1">{item.unit}</span>
                  </td>
                  <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                    {item.unit_cost ? formatCurrency(item.unit_cost) : '-'}
                  </td>
                  <td className={`px-4 py-3 text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                    {item.total_cost ? formatCurrency(item.total_cost) : '-'}
                  </td>
                  <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {item.supplier || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-right">
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className={isDark ? 'bg-gray-800' : 'bg-gray-100'}>
              <tr>
                <td colSpan={4} className={`px-4 py-3 text-right text-sm font-bold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                  Total Recipe Cost:
                </td>
                <td colSpan={3} className={`px-4 py-3 text-sm font-bold ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                  {formatCurrency(totalCost)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
